/**
 * @file useDeleteProfile.js
 * @module src/js/hooks/useDeleteProfile
 * @description
 * **A hook to delete a user profile by calling the backend API.**
 * - Utilizes React contexts to manage user state and modal messages.
 * - Returns a function that:
 *   - Prompts the user for confirmation before deletion.
 *   - Sends a DELETE request to `/api/profile/:id` endpoint.
 *   - Shows modal messages during the deletion process.
 *   - Handles logout upon successful deletion.
 *
 * @version
 * <p>
 *  2025-11-24: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useDeleteProfile hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useFetchFromBackend hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useModal } from "../contexts/ModalProvider";
import { useTheme } from "../contexts/ThemeProvider";
import useHandleLogout from "./useHandleLogout";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useDeleteProfile({ setDeleting } = {}) {
    const { theme } = useTheme();
    const { showModalMessage, confirm } = useModal();
    const handleLogout = useHandleLogout();
    const fetchFromBackend = useFetchFromBackend();

    if (typeof setDeleting !== "function") {
        throw new Error("setDeleting must be provided to useDeleteProfile.");
    }

    const deleteProfile = useCallback(async ({ id }) => {
        if (id == null) {
            throw new Error("id must be provided to deleteProfile.");
        } // Use loose equality (==) to catch both null and undefined
        

        try {
            setDeleting(true);

            // Ask the user first
            const ok = await confirm({
                message: "Are you sure you want to delete your account?\nOnly the administrator can undo this action.\nYou will be prompted to type a confirmation phrase to proceed.",
                confirmLabel: "Proceed",
                cancelLabel: "Cancel",
                buttonColor: theme === "dark" ? "lightcoral" : "red"
            });
            if (!ok) {
                showModalMessage({ type: "success", content: "Cancelled." });
                return;
            }
        
            // Ask for confirmation phrase
            const inputValue = await confirm({
                message: `To confirm deletion, please type 'DELETE ACCOUNT ${id}'.`,
                hasInput: true,
                confirmLabel: "Delete",
                cancelLabel: "Cancel",
                buttonColor: theme === "dark" ? "lightcoral" : "red",
                placeholder: `DELETE ACCOUNT ${id}`
            });
            if (inputValue === false) { // User cancelled
                showModalMessage({ type: "success", content: "Cancelled." });
                return;
            } // If inputValue !== placeholder, button is disabled, so no need to check for mismatch

            // User confirmed, now perform delete
            showModalMessage({ type: "info", content: "Deleting..." });
            
            const data = await fetchFromBackend({
                request: `/api/profile/${id}`,
                method: "DELETE",
                requireAuth: true,
                from: '/member-zone/profile',
            });

            if (data && data.success) {
                await handleLogout({ accountDeleted: true });
            } else if (data && data.error) {
                console.error("Failed to delete:", data.error);
                showModalMessage({ type: "error", content: 'Failed to delete. Please try again.' });
            }

        } catch (error) {
            console.error("An error occurred:", error);
            showModalMessage({ type: "error", content: 'An error occurred. Please try again.' });
            
        } finally {
            setDeleting(false);
        }
    }, [confirm, fetchFromBackend, handleLogout, showModalMessage, setDeleting, theme]);

    return deleteProfile;
}